import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  apiRequest,
  ApiError,
  buildQuery,
  getStoredToken,
  toListResult,
  unwrapItem,
} from "../api";
import { LANDLORD_ENDPOINTS } from "../types/roles";
import type { PaginationMeta } from "../types/roles";
import type {
  AgencyAgreement,
  AgencyAgreementPayload,
  AgencyAgreementQuery,
  AgencyAgreementState,
} from "../types/agencyAgreement";

const ENDPOINT = LANDLORD_ENDPOINTS.agencyAgreements;

interface TokenState {
  auth?: { access_token?: string | null };
  admin?: { access_token?: string | null };
}

const resolveToken = (state: TokenState) =>
  state.auth?.access_token ?? state.admin?.access_token ?? getStoredToken();

const errorMessage = (error: unknown) =>
  error instanceof ApiError || error instanceof Error
    ? error.message
    : "Something went wrong. Please try again.";

const EMPTY_META: PaginationMeta = {
  currentPage: 1,
  lastPage: 1,
  perPage: 15,
  total: 0,
};

const unwrapAgreement = (payload: unknown): AgencyAgreement => {
  const item = unwrapItem<Record<string, unknown>>(payload);
  const record = (item?.agreement ??
    item?.agency_agreement ??
    item) as AgencyAgreement | null;

  if (!record || typeof record !== "object") {
    throw new ApiError("The agreement could not be found.", 404);
  }

  return record;
};

export const fetchAgencyAgreements = createAsyncThunk<
  { items: AgencyAgreement[]; meta: PaginationMeta },
  { query?: AgencyAgreementQuery } | undefined,
  { state: TokenState; rejectValue: string }
>("agencyAgreement/fetchAll", async (payload, { getState, rejectWithValue }) => {
  try {
    const response = await apiRequest<unknown>(
      `${ENDPOINT}${buildQuery(payload?.query)}`,
      { token: resolveToken(getState()) }
    );

    return toListResult<AgencyAgreement>(response, [
      "agreements",
      "agency_agreements",
    ]);
  } catch (error) {
    return rejectWithValue(errorMessage(error));
  }
});

export const fetchAgencyAgreement = createAsyncThunk<
  AgencyAgreement,
  string | number,
  { state: TokenState; rejectValue: string }
>("agencyAgreement/fetchOne", async (id, { getState, rejectWithValue }) => {
  try {
    const response = await apiRequest<unknown>(`${ENDPOINT}/${id}`, {
      token: resolveToken(getState()),
    });

    return unwrapAgreement(response);
  } catch (error) {
    return rejectWithValue(errorMessage(error));
  }
});

export const createAgencyAgreement = createAsyncThunk<
  AgencyAgreement,
  AgencyAgreementPayload,
  { state: TokenState; rejectValue: string }
>("agencyAgreement/create", async (body, { getState, rejectWithValue }) => {
  try {
    const response = await apiRequest<unknown>(ENDPOINT, {
      method: "POST",
      token: resolveToken(getState()),
      body,
    });

    return unwrapAgreement(response);
  } catch (error) {
    return rejectWithValue(errorMessage(error));
  }
});

export const updateAgencyAgreement = createAsyncThunk<
  AgencyAgreement,
  { id: string | number; body: AgencyAgreementPayload },
  { state: TokenState; rejectValue: string }
>("agencyAgreement/update", async ({ id, body }, { getState, rejectWithValue }) => {
  try {
    const response = await apiRequest<unknown>(`${ENDPOINT}/${id}`, {
      method: "PUT",
      token: resolveToken(getState()),
      body,
    });

    return unwrapAgreement(response);
  } catch (error) {
    return rejectWithValue(errorMessage(error));
  }
});

const initialState: AgencyAgreementState = {
  items: [],
  meta: { ...EMPTY_META },
  loading: false,
  error: null,
  selected: null,
  selectedLoading: false,
  selectedError: null,
  submitting: false,
  success: null,
  feedbackError: null,
};

const agencyAgreementSlice = createSlice({
  name: "agencyAgreement",
  initialState,
  reducers: {
    selectAgencyAgreement(
      state,
      action: PayloadAction<AgencyAgreement | null>
    ) {
      state.selected = action.payload;
      state.selectedError = null;
    },
    clearAgencyAgreementFeedback(state) {
      state.success = null;
      state.feedbackError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAgencyAgreements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgencyAgreements.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.meta = action.payload.meta;
      })
      .addCase(fetchAgencyAgreements.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload ?? action.error.message ?? "Unable to load agreements.";
      })

      .addCase(fetchAgencyAgreement.pending, (state) => {
        state.selectedLoading = true;
        state.selectedError = null;
      })
      .addCase(fetchAgencyAgreement.fulfilled, (state, action) => {
        state.selectedLoading = false;
        state.selected = action.payload;
      })
      .addCase(fetchAgencyAgreement.rejected, (state, action) => {
        state.selectedLoading = false;
        state.selectedError =
          action.payload ?? action.error.message ?? "Unable to load agreement.";
      })

      .addCase(createAgencyAgreement.pending, (state) => {
        state.submitting = true;
        state.success = null;
        state.feedbackError = null;
      })
      .addCase(createAgencyAgreement.fulfilled, (state, action) => {
        state.submitting = false;
        state.success = "Agreement created successfully.";
        state.items = [action.payload, ...state.items];
        state.meta.total += 1;
      })
      .addCase(createAgencyAgreement.rejected, (state, action) => {
        state.submitting = false;
        state.feedbackError =
          action.payload ?? action.error.message ?? "Unable to create agreement.";
      })

      .addCase(updateAgencyAgreement.pending, (state) => {
        state.submitting = true;
        state.success = null;
        state.feedbackError = null;
      })
      .addCase(updateAgencyAgreement.fulfilled, (state, action) => {
        state.submitting = false;
        state.success = "Agreement updated successfully.";
        state.items = state.items.map((item) =>
          String(item.id) === String(action.payload.id) ? action.payload : item
        );

        if (
          state.selected &&
          String(state.selected.id) === String(action.payload.id)
        ) {
          state.selected = action.payload;
        }
      })
      .addCase(updateAgencyAgreement.rejected, (state, action) => {
        state.submitting = false;
        state.feedbackError =
          action.payload ?? action.error.message ?? "Unable to update agreement.";
      });
  },
});

export const { selectAgencyAgreement, clearAgencyAgreementFeedback } =
  agencyAgreementSlice.actions;

export default agencyAgreementSlice.reducer;
